<div class="statement-generator-container">
  <div class="card">
    <div class="card-header">
      <h4>
        <mat-icon class="mr-2">auto_awesome</mat-icon>
        {{ 'statement-generator.title' | translate }}
      </h4>
    </div>

    <div class="card-body">
      <!-- Input Selection Section -->
      <div class="section mb-4">
        <h5 class="section-title">
          <mat-icon class="mr-2">input</mat-icon>
          {{ 'statement-generator.select-inputs' | translate }}
        </h5>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <button
            type="button"
            class="btn btn-outline-primary"
            style="border-color: #6c5ce7; color: #6c5ce7;"
            (click)="openInputSelector()">
            <mat-icon class="mr-1">add</mat-icon>
            {{ 'statement-generator.add-inputs-button' | translate }}
          </button>

          <button
            *ngIf="hasUserInputs"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            (click)="clearAllInputs()">
            <mat-icon class="mr-1">clear_all</mat-icon>
            {{ 'generic.clear-all' | translate }}
          </button>
        </div>

        <!-- Selected Inputs List -->
        <div *ngIf="selectedInputs.length > 0" class="selected-inputs-container">
          <div class="alert alert-info mb-3">
            <mat-icon class="mr-2">info</mat-icon>
            <strong>{{ selectedInputs.length }}</strong>
            {{ selectedInputs.length === 1 ? ('statement-generator.input-selected' | translate) : ('statement-generator.inputs-selected' | translate) }}
          </div>

          <div class="inputs-list">
            <div
              *ngFor="let input of selectedInputs; let i = index"
              class="input-item-card"
              [class.context-input]="input.type === 'project_details' || input.type === 'pico'">
              <div class="input-icon">
                <mat-icon [class]="'type-' + input.type">{{ getInputIcon(input.type) }}</mat-icon>
              </div>
              <div class="input-details">
                <div class="input-name">{{ input.name }}</div>
                <div class="input-meta">
                  <span class="badge badge-secondary">{{ getInputTypeLabel(input.type) }}</span>
                  <span class="ml-2 text-muted" *ngIf="input.metadata?.participants">
                    {{ input.metadata.participants }} {{ 'statement-generator.participants' | translate }}
                  </span>
                  <span class="ml-2 text-muted" *ngIf="input.metadata?.uploadDate">
                    {{ input.metadata.uploadDate | date:'dd/MM/yyyy' }}
                  </span>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                *ngIf="input.type !== 'project_details' && input.type !== 'pico'"
                (click)="removeInput(i)">
                <mat-icon>close</mat-icon>
              </button>
              <span class="badge badge-info" *ngIf="input.type === 'project_details' || input.type === 'pico'">
                {{ 'statement-generator.auto-included' | translate }}
              </span>
            </div>
          </div>
        </div>

        <!-- No Inputs Selected -->
        <div *ngIf="selectedInputs.length === 0" class="alert alert-warning">
          <mat-icon class="mr-2">info</mat-icon>
          {{ 'statement-generator.no-inputs-selected-message' | translate }}
        </div>
      </div>

      <!-- Generate Button Section -->
      <div class="section mb-4 text-center">
        <button
          type="button"
          class="btn btn-lg btn-generate"
          [disabled]="selectedInputs.length === 0 || isGenerating"
          (click)="generateStatements()">
          <mat-icon class="mr-2" *ngIf="!isGenerating">auto_awesome</mat-icon>
          <mat-spinner diameter="20" class="mr-2" *ngIf="isGenerating" [style.display]="'inline-block'"></mat-spinner>
          {{ isGenerating ? ('statement-generator.generating' | translate) : ('statement-generator.generate-button' | translate) }}
        </button>

        <!-- Progress Bar -->
        <div *ngIf="isGenerating" class="generation-progress mt-3">
          <mat-progress-bar mode="determinate" [value]="generationProgress" color="primary"></mat-progress-bar>
          <p class="text-muted mt-2">{{ generationMessage }}</p>
        </div>
      </div>

      <!-- Needs Input Warning Section -->
      <div *ngIf="needsInput" class="section mb-4">
        <div class="alert alert-warning needs-input-alert">
          <div class="alert-header">
            <mat-icon class="mr-2" style="vertical-align: middle;">info</mat-icon>
            <strong>{{ 'statement-generator.needs-more-data-title' | translate }}</strong>
          </div>

          <p class="mt-2 mb-3">{{ needsInputMessage }}</p>

          <!-- Suggestions List -->
          <div *ngIf="needsInputSuggestions.length > 0" class="suggestions-container">
            <p class="mb-2"><strong>{{ 'statement-generator.suggestions' | translate }}:</strong></p>
            <ul class="suggestions-list">
              <li *ngFor="let suggestion of needsInputSuggestions" class="suggestion-item">
                <mat-icon class="suggestion-icon">lightbulb</mat-icon>
                {{ suggestion }}
              </li>
            </ul>
          </div>

          <!-- Action Buttons -->
          <div class="mt-3 d-flex gap-2">
            <button
              type="button"
              class="btn btn-primary"
              (click)="openInputSelector()">
              <mat-icon class="mr-1">add</mat-icon>
              {{ 'statement-generator.add-more-sources' | translate }}
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              (click)="retryGeneration()">
              <mat-icon class="mr-1">close</mat-icon>
              {{ 'generic.buttons.dismiss' | translate }}
            </button>
          </div>
        </div>
      </div>

      <!-- Generated Statements Results -->
      <div *ngIf="generatedStatements.length > 0" class="section">
        <div class="mb-3">
          <h5 class="section-title">
            <mat-icon class="mr-2">fact_check</mat-icon>
            {{ 'statement-generator.generated-statements' | translate }} ({{ generatedStatements.length }})
          </h5>
        </div>

        <!-- Warning: Auto-save disabled -->
        <div *ngIf="!enableAutoSave" class="alert alert-warning mb-3">
          <mat-icon class="mr-2">info</mat-icon>
          <strong>{{ 'statement-generator.auto-save-disabled-title' | translate }}</strong>
          <p class="mb-0">{{ 'statement-generator.auto-save-disabled-message' | translate }}</p>
        </div>

        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 5%;">#</th>
                <th style="width: 25%;">{{ 'statement-generator.table.statement' | translate }}</th>
                <th style="width: 10%;">{{ 'statement-generator.table.section' | translate }}</th>
                <th style="width: 15%;">{{ 'statement-generator.table.comment' | translate }}</th>
                <th style="width: 8%;">{{ 'statement-generator.table.loe' | translate }}</th>
                <th style="width: 8%;">{{ 'statement-generator.table.gor' | translate }}</th>
                <th style="width: 8%;">{{ 'statement-generator.table.references' | translate }}</th>
                <th style="width: 15%;">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let statement of generatedStatements; let i = index">
                <td class="text-center">
                  <span class="statement-number">{{ i + 1 }}</span>
                </td>
                <td>
                  <div class="statement-text">{{ statement.statement_text }}</div>
                  <div *ngIf="statement.id" class="text-muted small mt-1">
                    <mat-icon class="tiny-icon">check_circle</mat-icon>
                    {{ 'statement-generator.saved' | translate }}
                  </div>
                </td>
                <td>
                  <div class="section-text">
                    <span class="badge badge-info" *ngIf="statement.section">{{ statement.section }}</span>
                    <span class="text-muted" *ngIf="!statement.section">-</span>
                  </div>
                </td>
                <td>
                  <div class="comment-text text-muted">{{ statement.comment }}</div>
                </td>
                <td class="text-center">
                  <span class="badge" [ngClass]="getLoEBadgeClass(statement.level_of_evidence)">
                    {{ statement.level_of_evidence }}
                  </span>
                  <div class="small text-muted mt-1">LoE</div>
                </td>
                <td class="text-center">
                  <span class="badge" [ngClass]="getGoRBadgeClass(statement.grade_of_recommendation)">
                    {{ statement.grade_of_recommendation }}
                  </span>
                  <div class="small text-muted mt-1">GoR</div>
                </td>
                <td class="text-center">
                  <div *ngIf="statement.reference_links && statement.reference_links.length > 0" class="reference-links-container">
                    <a
                      *ngFor="let link of statement.reference_links"
                      [href]="link"
                      target="_blank"
                      class="reference-link"
                      title="Apri riferimento">
                      <mat-icon>open_in_new</mat-icon>
                    </a>
                  </div>
                  <span *ngIf="!statement.reference_links || statement.reference_links.length === 0" class="text-muted">
                    -
                  </span>
                </td>
                <td class="text-center actions-column">
                  <button
                    type="button"
                    class="btn btn-sm btn-delete-statement"
                    (click)="deleteStatement(statement)"
                    style="background-color: #e74c3c; color: white; border: none; padding: 8px 14px;"
                    title="Elimina statement">
                    <mat-icon style="vertical-align: middle;">delete</mat-icon>
                    <!-- <span class="ml-1">Elimina</span> -->
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- No Results Yet -->
      <div *ngIf="!isGenerating && generatedStatements.length === 0 && selectedInputs.length > 0" class="text-center py-5">
        <mat-icon style="font-size: 4rem; color: #ccc; height: 4rem; width: 4rem;">description</mat-icon>
        <p class="text-muted mt-3">{{ 'statement-generator.no-statements-yet' | translate }}</p>
      </div>
    </div>
  </div>
</div>
